import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  // Base — same across all variants. Focus ring uses the accent color so it
  // matches the brand. Disabled state is opacity-only (don't darken).
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ' +
    'ring-offset-background transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary — slate-900 on light, slate-50 on dark (the workhorse).
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        // Destructive — only for irreversible actions. Rose/red.
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Outline — secondary action with visual weight.
        outline: 'border border-border bg-surface hover:bg-surface-muted hover:text-foreground',
        // Secondary — same as outline but no border.
        secondary: 'bg-surface-muted text-foreground hover:bg-surface-subtle',
        // Ghost — tertiary, no chrome until hover.
        ghost: 'hover:bg-surface-muted hover:text-foreground',
        // Link — looks like a text link, no chrome at all.
        link: 'text-accent underline-offset-4 hover:underline',
        // Accent — brand violet. Use for the ONE primary CTA per screen.
        accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as a different element (e.g. anchor) while keeping styles. */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { buttonVariants };
